import { useCallback, useEffect, useRef, useState } from 'react';
import { presenceService } from '@/services';
import { PresenceRow } from '@/types/presence';
import { useAuthenticatedWebSocket } from '@/hooks/useAuthenticatedWebSocket';

interface PresenceWebSocketMessage {
  type: string;
  rows?: PresenceRow[];
}

const usePresenceWebSocket = (
  enabled: boolean,
  onPresenceMessage: (message: PresenceWebSocketMessage) => void
) => {
  const onPresenceMessageRef = useRef(onPresenceMessage);

  useEffect(() => {
    onPresenceMessageRef.current = onPresenceMessage;
  }, [onPresenceMessage]);

  const handleMessage = useCallback((raw: unknown) => {
    onPresenceMessageRef.current(raw as PresenceWebSocketMessage);
  }, []);

  return useAuthenticatedWebSocket(
    enabled,
    '/ws/presence/',
    handleMessage,
    'PresenceWebSocket'
  );
};

function presenceRowsToMap(rows: PresenceRow[]): Record<string, PresenceRow> {
  return Object.fromEntries(rows.map(row => [String(row.user_id), row]));
}

export function usePresenceSnapshot(enabled: boolean) {
  const [presenceByUserId, setPresenceByUserId] = useState<Record<string, PresenceRow>>({});

  const applyPresenceRows = useCallback((rows: PresenceRow[]) => {
    setPresenceByUserId(presenceRowsToMap(rows));
  }, []);

  const refetchPresenceSnapshot = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      const rows = await presenceService.getPresenceSnapshot();
      applyPresenceRows(rows);
    } catch {
      // Keep the last snapshot when REST is temporarily unavailable (e.g. during token refresh).
    }
  }, [applyPresenceRows, enabled]);

  const handlePresenceMessage = useCallback(
    (message: PresenceWebSocketMessage) => {
      if (
        (message.type === 'presence_snapshot' || message.type === 'presence_changed') &&
        message.rows
      ) {
        applyPresenceRows(message.rows);
      }
    },
    [applyPresenceRows]
  );

  const { isConnected } = usePresenceWebSocket(enabled, handlePresenceMessage);
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setPresenceByUserId({});
      wasConnectedRef.current = false;
      return;
    }

    void refetchPresenceSnapshot();
  }, [enabled, refetchPresenceSnapshot]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isConnected && !wasConnectedRef.current) {
      void refetchPresenceSnapshot();
    }
    wasConnectedRef.current = isConnected;
  }, [enabled, isConnected, refetchPresenceSnapshot]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const refreshOnPageShow = () => {
      void refetchPresenceSnapshot();
    };

    window.addEventListener('pageshow', refreshOnPageShow);
    return () => {
      window.removeEventListener('pageshow', refreshOnPageShow);
    };
  }, [enabled, refetchPresenceSnapshot]);

  return {
    presenceByUserId,
    isConnected,
    refetchPresenceSnapshot,
  };
}
